import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { buildApiUrl, buildAuthHeaders } from '../config/api';
import { AuthContext } from '../contexts/AuthContext';
import './UpdateMenuPage.css';

const EMPTY_ITEM = {
    name: '',
    description: '',
    price: '',
    image: 'menu-images/daily-special-board.webp',
    category: '',
};

const EMPTY_VARIANT = {
    name: '',
    price: '',
};

const DEFAULT_CATEGORIES = ['specials', 'quick bites', 'mains', 'desserts', 'sides'];
const MENU_IMAGE_LIBRARY = [
    { value: 'menu-images/daily-special-board.webp', label: 'Daily special board' },
    { value: 'menu-images/curries.webp', label: 'Curries' },
    { value: 'menu-images/pastas.webp', label: 'Pastas' },
    { value: 'menu-images/samoosas.webp', label: 'Samoosas' },
    { value: 'menu-images/puri-patha.webp', label: 'Puri patha' },
    { value: 'menu-images/patha-rolls.webp', label: 'Patha rolls' },
    { value: 'menu-images/hot-chips.webp', label: 'Hot chips' },
    { value: 'menu-images/bhajia.webp', label: 'Bhajia' },
    { value: 'menu-images/toasties.webp', label: 'Toasties' },
    { value: 'menu-images/rice.webp', label: 'Rice' },
    { value: 'menu-images/soji.webp', label: 'Soji' },
    { value: 'menu-images/vermicelli-papad.webp', label: 'Vermicelli and papad' },
    { value: 'menu-images/paneer-tikka-burger.webp', label: 'Paneer tikka burger' },
    { value: 'menu-images/pasta-veg-mushroom.webp', label: 'Vegetable mushroom pasta' },
    { value: 'menu-images/stir-fry-rice.webp', label: 'Stir-fry vegetables and rice' },
    { value: 'menu-images/veggie-burger-chips.webp', label: 'Veggie burger and chips' },
    { value: 'biryani.jpg', label: 'Biryani' },
    { value: 'burger.jpg', label: 'Burger' },
    { value: 'curry.jpg', label: 'Curry' },
    { value: 'pasta.jpg', label: 'Pasta' },
    { value: 'potatobake.jpg', label: 'Potato bake' },
    { value: 'samoosa.jpg', label: 'Samoosas' },
    { value: 'toastie.jpg', label: 'Toastie' },
    { value: 'tramezzini.jpg', label: 'Tramezzini' },
];

const flattenVariants = (variants) => {
    if (Array.isArray(variants)) {
        return variants;
    }

    if (!variants || typeof variants !== 'object') {
        return [];
    }

    return Object.keys(variants).reduce((accumulator, key) => {
        const variantGroup = variants[key];
        return accumulator.concat(Array.isArray(variantGroup) ? variantGroup : []);
    }, []);
};

const normalizeMenuItem = (item) => ({
    ...item,
    variants: flattenVariants(item.variants).sort((left, right) => left.name.localeCompare(right.name)),
});

const getAxiosConfig = () => ({
    headers: buildAuthHeaders({
        'Content-Type': 'application/json',
    }),
});

const getErrorMessage = (error, fallbackMessage) => {
    return error?.response?.data?.error || error?.message || fallbackMessage;
};

const resolveImageSource = (image) => {
    if (!image) {
        return '';
    }

    if (/^https?:\/\//i.test(image)) {
        return image;
    }

    return `${process.env.PUBLIC_URL}/${image.replace(/^\/+/, '')}`;
};

function MenuImagePicker({ value, onChange }) {
    return <div className="menu-image-picker">
        <span>Image library</span>
        <div className="menu-image-picker__grid">
            {MENU_IMAGE_LIBRARY.map((image) => <button
                key={image.value}
                type="button"
                className={value === image.value ? 'is-selected' : ''}
                onClick={() => onChange(image.value)}
                title={image.label}
            ><img src={resolveImageSource(image.value)} alt="" /><small>{image.label}</small></button>)}
        </div>
        <label className="menu-image-picker__advanced">
            Image URL or filename
            <input value={value} onChange={(event) => onChange(event.target.value)} placeholder="Choose above, paste a URL, or use any public asset filename" />
        </label>
    </div>;
}

function UpdateMenuPage() {
    const { user } = useContext(AuthContext);
    const [menuItems, setMenuItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [editingItem, setEditingItem] = useState(null);
    const [editingVariant, setEditingVariant] = useState(null);
    const [newItem, setNewItem] = useState(EMPTY_ITEM);
    const [newVariant, setNewVariant] = useState(EMPTY_VARIANT);
    const [pendingDelete, setPendingDelete] = useState(null);

    useEffect(() => {
        refreshMenuItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refreshMenuItems = async (itemIdToReselect = null) => {
        setLoading(true);
        try {
            const response = await axios.get(buildApiUrl('/api/menu'));
            const items = (Array.isArray(response.data) ? response.data : []).map(normalizeMenuItem);

            setMenuItems(items);
            setErrorMessage('');

            const nextSelectedId = itemIdToReselect || editingItem?.id;

            if (nextSelectedId) {
                const refreshedItem = items.find((item) => item.id === nextSelectedId) || null;
                setEditingItem(refreshedItem);

                if (editingVariant && refreshedItem) {
                    const refreshedVariant = refreshedItem.variants.find((variant) => variant.id === editingVariant.id) || null;
                    setEditingVariant(refreshedVariant);
                } else {
                    setEditingVariant(null);
                }
            }
        } catch (error) {
            console.error(error);
            setErrorMessage(getErrorMessage(error, 'We could not load the menu items right now. Please refresh and try again.'));
        } finally {
            setLoading(false);
        }
    };

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setErrorMessage('');
    };

    const handleFieldChange = (setter) => (event) => {
        const { name, value } = event.target;
        setter((currentState) => ({
            ...currentState,
            [name]: value,
        }));
    };

    const handleEditItemClick = (item) => {
        setEditingItem({
            ...item,
            variants: item.variants || [],
        });
        setEditingVariant(null);
        setNewVariant(EMPTY_VARIANT);
        setSuccessMessage('');
        setErrorMessage('');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleAddItem = async (event) => {
        event.preventDefault();
        setSaving(true);

        try {
            const payload = {
                ...newItem,
                price: Number(newItem.price || 0),
            };

            await axios.post(buildApiUrl('/api/menu'), payload, getAxiosConfig());
            setNewItem(EMPTY_ITEM);
            showSuccess('Menu item added successfully.');
            await refreshMenuItems();
        } catch (error) {
            console.error(error);
            setErrorMessage(getErrorMessage(error, 'Failed to add menu item.'));
        } finally {
            setSaving(false);
        }
    };

    const handleUpdateItem = async (event) => {
        event.preventDefault();

        if (!editingItem) {
            return;
        }

        setSaving(true);

        try {
            const payload = {
                ...editingItem,
                price: Number(editingItem.price || 0),
            };

            await axios.put(buildApiUrl(`/api/menu/${editingItem.id}`), payload, getAxiosConfig());
            showSuccess('Menu item updated successfully.');
            await refreshMenuItems(editingItem.id);
        } catch (error) {
            console.error(error);
            setErrorMessage(getErrorMessage(error, 'Failed to update menu item.'));
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteItem = async (itemId) => {
        setSaving(true);

        try {
            await axios.delete(buildApiUrl(`/api/menu/${itemId}`), getAxiosConfig());

            if (editingItem?.id === itemId) {
                setEditingItem(null);
                setEditingVariant(null);
            }

            showSuccess('Menu item deleted successfully.');
            setPendingDelete(null);
            await refreshMenuItems();
        } catch (error) {
            console.error(error);
            setErrorMessage(getErrorMessage(error, 'Failed to delete menu item.'));
        } finally {
            setSaving(false);
        }
    };

    const handleAddVariant = async (event) => {
        event.preventDefault();

        if (!editingItem) {
            setErrorMessage('Select a menu item before adding a variant.');
            return;
        }

        setSaving(true);

        try {
            const payload = {
                ...newVariant,
                price: Number(newVariant.price),
                menuItemId: editingItem.id,
            };

            await axios.post(buildApiUrl('/api/menu/variant'), payload, getAxiosConfig());
            setNewVariant(EMPTY_VARIANT);
            showSuccess('Variant added successfully.');
            await refreshMenuItems(editingItem.id);
        } catch (error) {
            console.error(error);
            setErrorMessage(getErrorMessage(error, 'Failed to add variant.'));
        } finally {
            setSaving(false);
        }
    };

    const handleUpdateVariant = async (event) => {
        event.preventDefault();

        if (!editingVariant || !editingItem) {
            return;
        }

        setSaving(true);

        try {
            const payload = {
                ...editingVariant,
                price: Number(editingVariant.price),
            };

            await axios.put(buildApiUrl(`/api/menu/variant/${editingVariant.id}`), payload, getAxiosConfig());
            showSuccess('Variant updated successfully.');
            await refreshMenuItems(editingItem.id);
        } catch (error) {
            console.error(error);
            setErrorMessage(getErrorMessage(error, 'Failed to update variant.'));
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteVariant = async (variantId) => {
        if (!editingItem) {
            return;
        }

        setSaving(true);

        try {
            await axios.delete(buildApiUrl(`/api/menu/variant/${variantId}`), getAxiosConfig());

            if (editingVariant?.id === variantId) {
                setEditingVariant(null);
            }

            showSuccess('Variant deleted successfully.');
            setPendingDelete(null);
            await refreshMenuItems(editingItem.id);
        } catch (error) {
            console.error(error);
            setErrorMessage(getErrorMessage(error, 'Failed to delete variant.'));
        } finally {
            setSaving(false);
        }
    };

    const groupedItems = menuItems.reduce((accumulator, item) => {
        const category = item.category || 'uncategorized';

        if (!accumulator[category]) {
            accumulator[category] = [];
        }

        accumulator[category].push(item);
        return accumulator;
    }, {});

    const categoryOptions = Array.from(new Set([
        ...DEFAULT_CATEGORIES,
        ...menuItems.map((item) => item.category).filter(Boolean),
    ])).sort((left, right) => left.localeCompare(right));

    if (!user || !['admin', 'superadmin'].includes(user.role)) {
        return (
            <div className="update-menu-page">
                <div className="menu-admin-card">
                    <h2>Update Menu</h2>
                    <p>You need an admin account to manage the menu.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="update-menu-page">
            <datalist id="menu-category-options">
                {categoryOptions.map((category) => (
                    <option key={category} value={category} />
                ))}
            </datalist>

            <div className="menu-admin-card menu-admin-hero">
                <div>
                    <p className="menu-admin-kicker">Menu management</p>
                    <h2>Update Menu</h2>
                    <p className="menu-admin-note">
                        Manage sellable menu items, their images, categories and customer-facing options. Each option has its own price.
                    </p>
                </div>
                <div className="menu-admin-user">
                    Signed in as <strong>{user.username}</strong>
                </div>
            </div>

            {(errorMessage || successMessage) && (
                <div className={`menu-admin-card status-card ${errorMessage ? 'error' : 'success'}`}>
                    {errorMessage || successMessage}
                </div>
            )}

            {pendingDelete && (
                <div className="menu-admin-card status-card error" role="alert">
                    <p>Delete {pendingDelete.label}? This cannot be undone.</p>
                    <div className="menu-admin-actions">
                        <button type="button" className="secondary-button" onClick={() => setPendingDelete(null)}>Cancel</button>
                        <button
                            type="button"
                            className="danger-button"
                            disabled={saving}
                            onClick={() => pendingDelete.type === 'item'
                                ? handleDeleteItem(pendingDelete.id)
                                : handleDeleteVariant(pendingDelete.id)}
                        >
                            {saving ? 'Deleting...' : 'Confirm delete'}
                        </button>
                    </div>
                </div>
            )}

            <div className="menu-admin-layout">
                <div className="menu-admin-column">
                    <div className="menu-admin-card">
                        <h3>Add New Menu Item</h3>
                        <form onSubmit={handleAddItem} className="menu-admin-form">
                            <label>
                                Name
                                <input name="name" value={newItem.name} onChange={handleFieldChange(setNewItem)} required />
                            </label>
                            <label>
                                Description
                                <textarea name="description" rows="4" value={newItem.description} onChange={handleFieldChange(setNewItem)} required />
                            </label>
                            <label>
                                Direct price
                                <input type="number" min="0" step="0.01" name="price" value={newItem.price} onChange={handleFieldChange(setNewItem)} placeholder="Only used when there are no options" />
                            </label>
                            <MenuImagePicker value={newItem.image} onChange={(image) => setNewItem((current) => ({ ...current, image }))} />
                            <label>
                                Category
                                <input
                                    name="category"
                                    list="menu-category-options"
                                    value={newItem.category}
                                    onChange={handleFieldChange(setNewItem)}
                                    placeholder="specials"
                                    required
                                />
                            </label>
                            <button type="submit" className="primary-button" disabled={saving}>
                                {saving ? 'Saving...' : 'Add Item'}
                            </button>
                        </form>
                    </div>

                    <div className="menu-admin-card">
                        <div className="menu-admin-section-heading">
                            <h3>Existing Menu Items</h3>
                            <button type="button" className="secondary-button" onClick={() => refreshMenuItems()} disabled={loading}>
                                Refresh
                            </button>
                        </div>

                        {loading ? (
                            <p>Loading menu items...</p>
                        ) : Object.keys(groupedItems).length === 0 ? (
                            <p>No menu items were returned by the API.</p>
                        ) : (
                            Object.keys(groupedItems).sort((left, right) => left.localeCompare(right)).map((category) => (
                                <div key={category} className="category-section">
                                    <h4>{category}</h4>
                                    <div className="menu-item-list">
                                        {groupedItems[category].map((item) => (
                                            <div key={item.id} className="menu-admin-item-card">
                                                <div className="menu-admin-item-copy">
                                                    <p className="item-id">#{item.id}</p>
                                                    <h5>{item.name}</h5>
                                                    <p>{item.description}</p>
                                                    <p>Base price: R{item.price}</p>
                                                    <p>Variants: {item.variants.length}</p>
                                                </div>
                                                {item.image && (
                                                    <img
                                                        className="menu-admin-image"
                                                        src={resolveImageSource(item.image)}
                                                        alt={item.name}
                                                        onError={(event) => {
                                                            event.currentTarget.style.display = 'none';
                                                        }}
                                                    />
                                                )}
                                                <div className="menu-admin-actions">
                                                    <button type="button" className="secondary-button" onClick={() => handleEditItemClick(item)}>
                                                        Manage
                                                    </button>
                                                    <button type="button" className="danger-button" onClick={() => setPendingDelete({ type: 'item', id: item.id, label: `${item.name} and its variants` })}>
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div className="menu-admin-column">
                    <div className="menu-admin-card">
                        <div className="menu-admin-section-heading">
                            <h3>{editingItem ? `Edit Item: ${editingItem.name}` : 'Select an item to edit'}</h3>
                            {editingItem && (
                                <button
                                    type="button"
                                    className="secondary-button"
                                    onClick={() => {
                                        setEditingItem(null);
                                        setEditingVariant(null);
                                        setNewVariant(EMPTY_VARIANT);
                                    }}
                                >
                                    Clear
                                </button>
                            )}
                        </div>

                        {!editingItem ? (
                            <p>Choose a menu item from the list to edit its details and manage its variants.</p>
                        ) : (
                            <>
                                <form onSubmit={handleUpdateItem} className="menu-admin-form">
                                    <label>
                                        Name
                                        <input name="name" value={editingItem.name} onChange={handleFieldChange(setEditingItem)} required />
                                    </label>
                                    <label>
                                        Description
                                        <textarea name="description" rows="4" value={editingItem.description} onChange={handleFieldChange(setEditingItem)} required />
                                    </label>
                                    <label>
                                        Direct price
                                        <input type="number" min="0" step="0.01" name="price" value={editingItem.price} onChange={handleFieldChange(setEditingItem)} placeholder="Only used when there are no options" />
                                    </label>
                                    <MenuImagePicker value={editingItem.image} onChange={(image) => setEditingItem((current) => ({ ...current, image }))} />
                                    <label>
                                        Category
                                        <input name="category" list="menu-category-options" value={editingItem.category} onChange={handleFieldChange(setEditingItem)} required />
                                    </label>
                                    <button type="submit" className="primary-button" disabled={saving}>
                                        {saving ? 'Saving...' : 'Update Item'}
                                    </button>
                                </form>

                                <div className="variant-panel">
                                    <h4>Add customer option</h4>
                                    <form onSubmit={handleAddVariant} className="menu-admin-form compact">
                                        <label>
                                            Option name
                                            <input name="name" value={newVariant.name} onChange={handleFieldChange(setNewVariant)} required />
                                        </label>
                                        <label>
                                            Price
                                            <input type="number" min="0" step="0.01" name="price" value={newVariant.price} onChange={handleFieldChange(setNewVariant)} required />
                                        </label>
                                        <button type="submit" className="primary-button" disabled={saving}>
                                            Add option
                                        </button>
                                    </form>
                                </div>

                                <div className="variant-panel">
                                    <div className="menu-admin-section-heading">
                                        <h4>Customer options</h4>
                                        <span>{editingItem.variants.length} total</span>
                                    </div>
                                    {editingItem.variants.length === 0 ? (
                                        <p>This item currently uses its direct price. Add an option when customers must choose a serving, pack or flavour.</p>
                                    ) : (
                                        <div className="variant-list">
                                            {editingItem.variants.map((variant) => (
                                                <div key={variant.id} className="variant-card">
                                                    <div>
                                                        <strong>{variant.name}</strong>
                                                        <p>Price: R{variant.price}</p>
                                                    </div>
                                                    <div className="menu-admin-actions">
                                                        <button type="button" className="secondary-button" onClick={() => setEditingVariant({ ...variant })}>
                                                            Edit
                                                        </button>
                                                        <button type="button" className="danger-button" onClick={() => setPendingDelete({ type: 'variant', id: variant.id, label: variant.name })}>
                                                            Delete
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {editingVariant && (
                                    <div className="variant-panel">
                                        <div className="menu-admin-section-heading">
                                            <h4>Edit customer option</h4>
                                            <button type="button" className="secondary-button" onClick={() => setEditingVariant(null)}>
                                                Cancel
                                            </button>
                                        </div>
                                        <form onSubmit={handleUpdateVariant} className="menu-admin-form compact">
                                            <label>
                                                Option name
                                                <input name="name" value={editingVariant.name} onChange={handleFieldChange(setEditingVariant)} required />
                                            </label>
                                            <label>
                                                Price
                                                <input type="number" min="0" step="0.01" name="price" value={editingVariant.price} onChange={handleFieldChange(setEditingVariant)} required />
                                            </label>
                                            <button type="submit" className="primary-button" disabled={saving}>
                                                Update option
                                            </button>
                                        </form>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default UpdateMenuPage;
